import type { AreaComp, Comp, GameObj, PosComp } from "kaplay";
import { config } from "../config";
import { PX_PER_M } from "../config/constants";
import type { ModeConfig } from "../config/types";
import { emit } from "../core/events";
import { k } from "../core/kaplay";
import { heightAboveGround } from "../systems/air";
import { resolveHit } from "../systems/collision";
import type { ObstacleComp } from "./obstacle";
import type { Rider } from "./rider";

type Obstacle = GameObj<ObstacleComp | PosComp>;

const RIDER_HITBOX_H_PX = 32;
const UNSTICK_GAP_PX = 8;

function unstick(r: Rider, o: Obstacle) {
    r.motion = { ...r.motion, y: (o.pos.y + RIDER_HITBOX_H_PX + UNSTICK_GAP_PX) / PX_PER_M };
}

function hit(r: Rider, o: Obstacle): "large" | null {
    if (o.spent || r.state === "crashed" || r.state === "dead") return null;
    const { speed, heading } = r.motion;
    const result = resolveHit(o.obstacleType, o.obstacleConfig, speed, heading, heightAboveGround(r.air));

    if (result.kind === "clear") return null;
    if (result.kind === "hop") {
        o.spent = true;
        if (r.state === "riding") r.launch(result.speed);
        emit("jump", { source: o.obstacleType === "log" ? "lip" : "roller", strength: result.speed, pop: false });
        return null;
    }
    if (r.invulnerable > 0) return null;

    o.spent = true;
    r.motion = { ...r.motion, speed: speed * (1 - result.speedLoss) };
    emit("wipeout", { severity: result.severity, cause: o.obstacleType });
    if (result.severity === "small") return null;

    r.wipeout(result.severity);
    unstick(r, o);
    return result.severity === "large" ? "large" : null;
}

export function obstacleHits(mode: ModeConfig): Comp {
    const { count, windowS } = config.modes.severeCrash;
    let largeCrashTimes: number[] = [];

    return {
        id: "obstacleHits",
        require: ["rider", "area"],
        add(this: Rider & GameObj<AreaComp>) {
            this.onCollideUpdate("obstacle", (other) => {
                if (hit(this, other as Obstacle) !== "large" || !mode.severeCrashEnds) return;
                largeCrashTimes = [...largeCrashTimes, k.time()].filter((t) => k.time() - t <= windowS);
                if (largeCrashTimes.length >= count) emit("runEnd", { reason: "crash" });
            });
        },
    };
}
